/**
 * Storico (log audit) di TUTTE le release del prodotto, in fondo alla pagina prodotto: creazione
 * release, archiviazione/ripristino, bundle aggiunti, BOM allegati/rimossi, consegne in blocco ai
 * clienti.
 *
 * product-service audita queste mutazioni con id gerarchici `<prodotto>@<versione>[/<bundle>[/…]]`:
 * una sola lettura per prefisso `<prodotto>@` copre tutte le release. Visibile con VIEW_AUDIT_LOG.
 */

import { AuditTrail, AUDIT_ACTION_LABEL } from '../audit/AuditTrail.js';
import type { AuditEventRow } from '../audit/audit-api.js';
import { productKeys } from './hooks.js';

/** Tipi entità auditati sulle release (l'evento `inventory` duplica `component_bom`). */
export const RELEASE_AUDIT_ENTITY_TYPES = [
  'release',
  'release_component',
  'component_bom',
  'deployment',
];

const AZIONE: Record<string, Record<string, string>> = {
  release: {
    create: 'Release creata',
    modify: 'Release modificata',
    delete: 'Release eliminata',
    restore: 'Release ripristinata',
  },
  release_component: { create: 'Bundle aggiunto', delete: 'Bundle rimosso' },
  component_bom: { create: 'BOM allegato', delete: 'BOM rimosso' },
  deployment: { create: 'Consegna ai clienti' },
};

/** Etichetta dell'azione secondo il tipo di entità (fallback: etichetta generica). */
export function releaseAuditActionLabel(
  ev: Pick<AuditEventRow, 'entityType' | 'action'> & Partial<Pick<AuditEventRow, 'after'>>,
): string {
  // Archiviazione: `modify` con after.archived = true (POST …/archive).
  const after = ev.after as { archived?: unknown } | null | undefined;
  if (ev.entityType === 'release' && ev.action === 'modify' && after?.archived === true) {
    return 'Release archiviata';
  }
  return AZIONE[ev.entityType]?.[ev.action] ?? AUDIT_ACTION_LABEL[ev.action] ?? ev.action;
}

/**
 * Oggetto dell'evento dall'id gerarchico: `p@1.0` → "release 1.0", `p@1.0/3` → "release 1.0 ·
 * bundle #3", `p@1.0/3/7` → "release 1.0 · bundle #3 · allegato #7".
 */
export function releaseAuditSubject(productId: string, entityId: string): string {
  const prefisso = `${productId}@`;
  if (!entityId.startsWith(prefisso)) return entityId;
  const [versione, bundle, membership] = entityId.slice(prefisso.length).split('/');
  const parti = [`release ${versione}`];
  if (bundle) parti.push(`bundle #${bundle}`);
  if (membership) parti.push(`allegato #${membership}`);
  return parti.join(' · ');
}

export function ReleaseAuditTrail({ productId }: { productId: string }) {
  return (
    <AuditTrail
      queryKey={productKeys.releaseAudit(productId)}
      queries={[{ entityTypes: RELEASE_AUDIT_ENTITY_TYPES, entityIdPrefix: `${productId}@` }]}
      testId="storico-release"
      title="Storico delle release"
      defaultOpen
      actionLabel={releaseAuditActionLabel}
      describe={(ev) => releaseAuditSubject(productId, ev.entityId)}
    />
  );
}
